'use client';

import { useId } from 'react';
import { RadioGroup } from '@1debit/chimekit';
import { cn } from 'utils/styles';
import { HintDisclosure } from './HintDisclosure';

export interface RadioFieldOption<Key extends string> {
  key: Key;
  label: string;
  /** Opens in the shared hint modal from a trigger beside the label. */
  hint?: string;
}

interface RadioFieldProps<Key extends string> {
  legend: string;
  options: readonly RadioFieldOption<Key>[];
  value: Key;
  onChange: (option: RadioFieldOption<Key>) => void;
  /** Hides the legend visually; it stays the group's accessible name. */
  hideLegend?: boolean;
  /** About the question as a whole, where an option `hint` explains one answer. */
  hint?: string;
  className?: string;
}

export const RadioField = <Key extends string>({
  legend,
  options,
  value,
  onChange,
  hideLegend = false,
  hint,
  className,
}: RadioFieldProps<Key>) => {
  const legendId = useId();
  const hintIdPrefix = useId();
  const legendHintId = `${legendId}-hint`;
  const legendElement = (
    <p className={cn('text-body-saans', hideLegend && 'sr-only')} id={legendId}>
      {legend}
    </p>
  );

  return (
    <div className={className}>
      {hint ? (
        // Id on the text alone keeps the trigger out of the group's name; `<div>` since the disclosure renders a `<p>`.
        <div className="text-body-saans">
          <span id={legendId} className="mr-2">
            {legend}
          </span>
          <HintDisclosure hintId={legendHintId} label={legend} hint={hint} />
        </div>
      ) : (
        legendElement
      )}

      <RadioGroup
        value={value}
        onValueChange={(key: Key) => {
          const option = options.find(candidate => candidate.key === key);

          if (option) onChange(option);
        }}
        aria-labelledby={legendId}
        aria-describedby={hint ? legendHintId : undefined}
        className={cn('flex flex-row flex-wrap gap-6', !hideLegend && 'mt-3')}>
        {options.map(option => {
          const hintId = `${hintIdPrefix}-${option.key}`;

          return (
            <div key={option.key} className="flex items-center gap-2">
              {/* The trigger sits outside the label, which would otherwise toggle the radio on its click. */}
              <label className="flex cursor-pointer items-center gap-2 text-body-saans">
                {/* Described and focus-ringed on the radio itself: the label is not what takes focus, and
                    chimekit's item hard-codes `outline-hidden`, which beats any `outline-*` set here - so
                    the indicator has to be a ring. Kale rather than the `bg-tertiary` used elsewhere, which
                    at 1.05:1 against this card fails the 3:1 WCAG 1.4.11 asks of a focus indicator. */}
                <RadioGroup.Item
                  value={option.key}
                  aria-describedby={option.hint ? hintId : undefined}
                  className="focus-visible:ring-2 focus-visible:ring-kale focus-visible:ring-offset-2"
                />
                {option.label}
              </label>

              {option.hint && <HintDisclosure hintId={hintId} label={option.label} hint={option.hint} />}
            </div>
          );
        })}
      </RadioGroup>
    </div>
  );
};
